import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useCommonTranslation } from '../../hooks/useCommonTranslation';
import { usePortalName } from '../../hooks/usePortalName';
import authService from '../../services/authService';
import { useNavigate } from 'react-router-dom';
import { useIsClientPortal } from '../../hooks/useIsClientPortal';

const PREFIX = 'NoAccess';

const classes = {
  defaultContainer: `${PREFIX}-default-container`,
  content: `${PREFIX}-content`,
  container: `${PREFIX}-container`,
  left: `${PREFIX}-left`,
  right: `${PREFIX}-right`,
  logo: `${PREFIX}-logo`,
  formContainer: `${PREFIX}-formContainer`,
  title: `${PREFIX}-title`,
  body: `${PREFIX}-body`,
  submit: `${PREFIX}-submit`,
};

const portalName = usePortalName();
const heroImage = `${portalName}/auth_hero.webp`;
const logoSrc = `${portalName}/brand-logo-primary.svg`;

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.defaultContainer}`]: {
    display: 'flex',
    flexDirection: 'column',
    flex: '0 1 100%',
    background: 'white',
  },

  [`& .${classes.content}`]: {
    margin: theme.spacing(2),
  },

  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    height: '100vh',
    backgroundSize: 'cover',
    backgroundImage: `url(${heroImage})`,
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },

  [`& .${classes.left}`]: {
    flex: 1,
    display: 'flex',
    alignItems: 'start',
    justifyContent: 'center',
    paddingLeft: theme.spacing(10),
    paddingTop: theme.spacing(30),
    [theme.breakpoints.down('md')]: {
      paddingLeft: theme.spacing(2),
      justifyContent: 'center',
    },
  },

  [`& .${classes.right}`]: {
    flex: 1,
    display: 'flex',
    alignItems: 'start',
    justifyContent: 'center',
    paddingRight: theme.spacing(10),
    paddingTop: theme.spacing(20),
    paddingBottom: theme.spacing(3),
    overflowY: 'auto',
    [theme.breakpoints.down('md')]: {
      paddingRight: theme.spacing(2),
      justifyContent: 'center',
    },
  },

  [`& .${classes.logo}`]: {
    width: 'auto',
    height: 'auto',
    display: 'block',
    objectFit: 'contain',
    [theme.breakpoints.down('md')]: {
      maxWidth: 180,
      marginBottom: theme.spacing(4),
      marginTop: theme.spacing(4),
    },
  },

  [`& .${classes.formContainer}`]: {
    position: 'relative',
    width: 305,
    backgroundColor: 'white',
    padding: theme.spacing(4),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[5],
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },

  [`& .${classes.title}`]: {
    marginBottom: theme.spacing(3),
    marginTop: theme.spacing(1.5),
    width: '100%',
  },

  [`& .${classes.body}`]: {
    marginBottom: theme.spacing(2.5),
    textAlign: 'left',
    lineHeight: '150%',
  },

  [`& .${classes.submit}`]: {
    marginTop: theme.spacing(2),
    width: '100%',
  },
}));

export default function NoAccess() {
  const t = useCommonTranslation();
  const navigate = useNavigate();
  const isLoggedIn = authService.isLoggedIn();
  const theme = useTheme();
  const isLarge = useMediaQuery(theme.breakpoints.up('md'));
  const isPortal = portalName ? true : false;
  const isClientPortal = useIsClientPortal();
  const redirectPath = isClientPortal ? '/clientLogin' : '/login';

  const logout = () => {
    authService.logout().then(() => {
      navigate(redirectPath);
    });
  };

  if (!isPortal) {
    return (
      <Root className={classes.defaultContainer}>
        <Typography variant="h4" className={classes.content}>
          You do not have access to this application. Contact IT to get access.
        </Typography>
      </Root>
    );
  }

  return (
    <Root className={classes.container}>
      {isLarge && (
        <Box className={classes.left} component="header">
          <img
            alt={`${t('member.logoAlt')}`}
            src={logoSrc}
            className={classes.logo}
          />
        </Box>
      )}
      <Box className={classes.right} component="main">
        <Box className={classes.formContainer}>
          <Typography variant="h3" component="h1" className={classes.title}>
            {t('common.noAccessTitle')}
          </Typography>
          <Typography variant="body1" component="p" className={classes.body}>
            {t('common.noAccessMessage')}
          </Typography>
          <Button
            color="primary"
            variant="contained"
            size="large"
            className={classes.submit}
            onClick={logout}>
            {t('app.logout')}
          </Button>
        </Box>
      </Box>
    </Root>
  );
}
